import { useState, useCallback, useRef } from 'react';
import swal from 'sweetalert2';

/**
 * Opciones de SweetAlert2 para bloquear el botón de confirmación
 * y evitar doble clic durante operaciones masivas.
 */
export const getSwalSubmitGuardOptions = (loadingText = 'Enviando...') => ({
  allowOutsideClick: false,
  allowEscapeKey: false,
  preConfirm: () => {
    const confirmButton = swal.getConfirmButton();
    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.style.opacity = '0.6';
      confirmButton.style.cursor = 'not-allowed';
      confirmButton.textContent = loadingText;
    }
  },
});

/**
 * Hook reutilizable para evitar ejecuciones duplicadas por doble clic
 * en operaciones masivas o acciones críticas.
 */
const usePreventDoubleSubmit = (options = {}) => {
  const { loadingText = 'Procesando...' } = options;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const guardSubmit = useCallback(() => !isSubmittingRef.current, []);

  const startSubmit = useCallback(() => {
    isSubmittingRef.current = true;
    setIsSubmitting(true);
  }, []);

  const endSubmit = useCallback(() => {
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  }, []);

  const wrapCallbacks = useCallback((successCallback, errorCallback) => ({
    successCallback: (...args) => {
      endSubmit();
      if (successCallback) successCallback(...args);
    },
    errorCallback: (...args) => {
      endSubmit();
      if (errorCallback) errorCallback(...args);
    },
  }), [endSubmit]);

  const getButtonProps = useCallback((label, submittingLabel = loadingText) => ({
    disabled: isSubmitting,
    children: isSubmitting ? submittingLabel : label,
  }), [isSubmitting, loadingText]);

  return {
    isSubmitting,
    guardSubmit,
    startSubmit,
    endSubmit,
    wrapCallbacks,
    getButtonProps,
    getSwalSubmitGuardOptions,
  };
};

export default usePreventDoubleSubmit;
